import { useState } from 'react';
import { Table, TableProps, message } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import { useNavigate } from 'react-router-dom';
import InfoText from '../../../atoms/InfoText';
import ProductActionsDropdown from './ProductActionsDropdown';
import DeleteModal from '../../../atoms/DeleteModal';
import ActivityLogsPopup, {
  ActivityLogsPopupData,
} from '../../../AdminLogs/ActivityLogsPopup';

import { useDeleteSellerProductByAdminMutation } from '../../../../store/services/products';

export interface SellerProductRow {
  productId: string;
  productName: string;
  imageUrl?: string | null;
  createdBy?: string;
  availableQnt: number;
  dateCreated?: string;
  status?: {
    code:
      | 'APPROVED'
      | 'NEW'
      | 'ACTIVE'
      | 'REJECTED'
      | 'RETURNED'
      | 'PENDING'
      | 'DELETED'
      | string;
    name: string;
  };
  publishStatus?: {
    code: string;
    name: string;
  } | null;
  isPublish?: boolean;
  actions?: {
    canPreviewProduct?: boolean;
    canPromoteProduct?: boolean;
    canEditProduct?: boolean;
    canDuplicateProduct?: boolean;
    canDeleteProduct?: boolean;
    canPublishProduct?: boolean;
  };
  serial?: string;
  stockStatus?: {
    code: string;
    name: string;
  } | null;
}

interface SellerProductsTableProps {
  sellerId?: string;
  products?: SellerProductRow[];
  loading?: boolean;
  onRefresh?: () => void;
}

const STOCK_STATUS_COLORS: Record<string, string> = {
  IN_STOCK: '!text-light-success dark:!text-dark-success font-semibold',
  LOW_STOCK: '!text-light-alert dark:!text-dark-alert font-semibold',
  OUT_OF_STOCK: '!text-light-warning dark:!text-dark-warning font-semibold',
};

const APPROVAL_STATUS_COLORS: Record<string, string> = {
  APPROVED: '!text-light-success dark:!text-dark-success font-semibold',
  NEW: '!text-light-success dark:!text-dark-success font-semibold',
  ACTIVE: '!text-light-success dark:!text-dark-success font-semibold',
  REJECTED: '!text-light-warning dark:!text-dark-warning font-semibold',
  DELETED: '!text-light-warning dark:!text-dark-warning font-semibold',
  RETURNED: '!text-light-alert dark:!text-dark-alert font-semibold',
  PENDING: '!text-light-information dark:!text-dark-information font-semibold',
};

const PUBLISH_STATUS_COLORS: Record<string, string> = {
  PUBLISHED: '!text-light-success dark:!text-dark-success font-semibold',
  UNPUBLISHED: '!text-light-warning dark:!text-dark-warning font-semibold',
};

export default function SellerProductsTable({
  sellerId,
  products = [],
  loading = false,
  onRefresh,
}: SellerProductsTableProps) {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<string | undefined>(undefined);
  const [logsModalData, setLogsModalData] =
    useState<ActivityLogsPopupData | null>(null);

  const [deleteProduct, { isLoading: isDeleting }] = useDeleteSellerProductByAdminMutation();

  const handleDeleteClick = (id: string) => {
    setProductToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (productToDelete) {
      try {
        await deleteProduct(productToDelete).unwrap();
        message.success(t('sellers.products.deleteSuccess', 'Product deleted successfully'));
        setDeleteModalOpen(false);
        onRefresh?.();
      } catch (err) {
        message.error(t('sellers.products.deleteFailed', 'Failed to delete product'));
      }
    }
  };

  const columns: TableProps<SellerProductRow>['columns'] = [
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('sellers.products.image')}
        </span>
      ),
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 100,
      render: (imageUrl: string | null) => (
        <div
          className="w-10 h-10 relative rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0 border border-gray-200 dark:border-gray-600 flex items-center justify-center"
          data-testid="store-products-table-cell-image"
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="product"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gray-200 dark:bg-gray-600" data-testid="store-products-table-cell-image-placeholder" />
          )}
        </div>
      ),
    },
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('sellers.products.productName')}
        </span>
      ),
      dataIndex: 'productName',
      key: 'productName',
      ellipsis: true,
      render: (name: string) => (
        <span className="font-normal text-[#090909] dark:text-white text-sm">{name}</span>
      ),
    },
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('sellers.products.createdBy')}
        </span>
      ),
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: string) => (
        <InfoText text={createdBy || '—'} className="!text-[#090909] dark:!text-white font-normal" />
      ),
    },
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('sellers.products.availableQuantity')}
        </span>
      ),
      dataIndex: 'availableQnt',
      key: 'availableQnt',
      render: (qty: number) => (
        <InfoText
          text={`${qty ?? 0} ${t('sellers.products.unit')}`}
          className="!text-[#090909] dark:!text-white font-normal"
        />
      ),
    },
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('sellers.products.dateCreated')}
        </span>
      ),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      render: (dateCreated: string) => (
        <InfoText text={dateCreated ? dayjs(dateCreated).locale(language).format('DD MMM YYYY') : '—'} className="!text-[#090909] dark:!text-white font-normal" />
      ),
    },
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('sellers.products.status', 'Status')}
        </span>
      ),
      dataIndex: 'status',
      key: 'status',
      render: (status: SellerProductRow['status'], record: SellerProductRow) => {
        if (!status) return <InfoText text="—" className="!text-gray-400" />;
        const code = status.code?.toUpperCase();
        let displayValue = status.name;
        const isPublished = record.publishStatus?.code === 'PUBLISHED' || record.isPublish || false;
        if (code === 'ACTIVE' || code === 'APPROVED' || status.name === 'Published') {
          displayValue = isPublished ? t('sellers.products.published') : t('sellers.products.unpublished');
        } else if (code === 'PENDING') {
          displayValue = t('sellers.products.pending');
        } else if (code === 'REJECTED') {
          displayValue = t('sellers.products.rejected');
        } else if (code === 'RETURNED') {
          displayValue = t('sellers.products.returned');
        }
        return (
          <InfoText
            text={displayValue}
            className={
              (code === 'ACTIVE' || code === 'APPROVED' || status.name === 'Published')
                ? (isPublished ? APPROVAL_STATUS_COLORS.APPROVED : APPROVAL_STATUS_COLORS.REJECTED)
                : (APPROVAL_STATUS_COLORS[status.code] ?? '')
            }
          />
        );
      },
    },
    {
      title: (
        <span className="!font-semibold text-base leading-none text-[#090909] dark:text-white">
          {t('common.actions')}
        </span>
      ),
      key: 'actions',
      width: 100,
      render: (record: SellerProductRow) => {
        const isPublished = record.publishStatus?.code === 'PUBLISHED' || record.isPublish || false;
        return (
          <ProductActionsDropdown
            sellerId={sellerId}
            productId={record.productId}
            isPublished={isPublished}
            actions={record.actions}
            onDeleteClick={handleDeleteClick}
            onRefresh={onRefresh}
          />
        );
      },
    },
  ];

  return (
    <div data-testid="store-products-table">
      <div data-testid="store-products-table-antd-wrap">
        <Table<SellerProductRow>
          columns={columns}
          dataSource={products}
          rowKey="productId"
          className="customTable"
          rowClassName={(record, index) =>
            index % 2 !== 0
              ? 'cursor-pointer [&>td]:!bg-[#F9F9F9] dark:[&>td]:!bg-gray-800/50 [&>td:first-child]:rounded-l-[12px] [&>td:last-child]:rounded-r-[12px] [&>td]:transition-colors'
              : 'cursor-pointer [&>td]:!bg-white dark:[&>td]:!bg-gray-900 [&>td]:transition-colors'
          }
          onRow={(record) => ({
            onClick: () => {
              if (record?.status?.code?.toUpperCase() === 'DELETED') {
                setLogsModalData({
                  id: record?.productId,
                  type: 'PRODUCT',
                });
                return;
              }
              navigate(`/products/product-details/${record.productId}`, {
                state: { sellerId }
              });
            },
          })}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={false}
        />
      </div>

      <DeleteModal
        isOpen={deleteModalOpen}
        title={t('sellers.products.deleteConfirmTitle')}
        description={t('sellers.products.deleteConfirmContent')}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={handleConfirmDelete}
        isLoading={isDeleting}
        actionText={t('sellers.products.deleteProduct')}
      />

      <ActivityLogsPopup
        isOpen={Boolean(logsModalData)}
        onClose={() => setLogsModalData(null)}
        data={logsModalData}
      />
    </div>
  );
}
